import { Component, Element, h, Host, Listen, Prop, State } from "@stencil/core";
import { getViewportWidth } from "../../utils/getViewportWidth";
import { debounce } from "../../utils/debounce";

@Component({
    tag: "ux-comparison-table",
    styleUrl: "comparison-table.scss",
    shadow: false
})
export class ComparisonTable {
    @Element() host: HTMLUxComparisonTableElement;
    @State() windowWidth: number = getViewportWidth();
    @State() hasMinimumColWidth = false;

    /** Is the Table mainly fed by numeric data or by text content */
    @Prop() readonly dataTable: boolean = false;

    // looks for elements until selector is reached (e.g. look for all tr until the next .cat class)
    private nextUntil = (elem: HTMLElement, selector: string) => {
        const siblings = [];
        elem = elem.nextElementSibling as HTMLElement;

        while (elem) {
            if (elem.matches(selector)) {
                break;
            }
            siblings.push(elem);
            elem = elem.nextElementSibling as HTMLElement;
        }
        return siblings;
    };

    connectedCallback() {
        window.addEventListener("resize", this.handleResize);
        if (this.dataTable) {
            this.setColSpan();
        } else {
            this.setHeaders();
            this.checkMinimumColWidth();
        }
    }

    private setHeaders = () => {
        const tableRows = this.host.querySelectorAll("tr");
        tableRows.forEach((row) => {
            if (row.children.length === 1) {
                row.classList.add("cat");
            } else {
                row.classList.add("sub-cat");
            }
        });
    };

    private setColSpan = () => {
        const categories = this.host.querySelectorAll(".cat > td");

        if (this.windowWidth > 550) {
            const subCat = this.host.querySelector(".sub-cat");
            const colSpan = subCat.querySelectorAll("td").length;
            categories.forEach((category) => {
                category.setAttribute("colspan", colSpan.toString());
            });
        } else {
            categories.forEach((category) => {
                category.removeAttribute("colspan");
            });
        }
    };

    private handleResize = debounce(() => {
        this.windowWidth = getViewportWidth();
        this.checkMinimumColWidth();
        if (this.dataTable) {
            this.setColSpan();
        }
    }, 200);

    private checkMinimumColWidth = () => {
        if (this.dataTable) {
            this.hasMinimumColWidth = false;
            return;
        }
        const firstDataRow = this.host.querySelector("tr");
        const columnCount = firstDataRow.querySelectorAll("th:not(.first)").length;
        this.hasMinimumColWidth = this.host.getBoundingClientRect().width < columnCount * 250;
    };

    @Listen("click", { target: "window" })
    handleLinkClick(ev) {
        if (ev.target.hasAttribute("data-target")) {
            const target = ev.target.getAttribute("data-target");
            const targetElements = this.host.querySelectorAll(`[data-info="${target}"]`);

            if (ev.target.classList.contains("opened")) {
                ev.target.classList.remove("opened");
                targetElements.forEach((el) => {
                    el.classList.remove("opened");
                });
            } else {
                ev.target.classList.add("opened");
                targetElements.forEach((el) => {
                    el.classList.add("opened");
                });
            }
        }
    }

    private insertIcons = () => {
        const categories = Array.from(this.host.querySelectorAll(".cat .first"));
        categories.forEach((el) => {
            const icon = document.createElement("ux-icon");
            icon.setAttribute("name", "chevron-right");
            el.appendChild(icon);
        });
    };

    private insertDataTags = () => {
        const categories = Array.from(this.host.querySelectorAll<HTMLElement>(".cat"));

        categories.forEach((el, index) => {
            el.setAttribute("data-target", `cat-${index}`);
            const elementsBetween = this.nextUntil(el, ".cat");
            elementsBetween.forEach((element) => {
                element.setAttribute("data-info", `cat-${index}`);
            });
        });
    };

    componentDidLoad() {
        if (this.dataTable) {
            this.insertIcons();
            this.insertDataTags();
        }
    }

    disconnectedCallback() {
        window.removeEventListener("scroll", this.handleLinkClick);
        window.removeEventListener("resize", this.handleResize);
    }

    render() {
        const baseClass = "ux-comparison-table";

        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--data`]: this.dataTable,
                    [`${baseClass}--content`]: !this.dataTable,
                    [`${baseClass}--fixed-column-width`]: this.hasMinimumColWidth
                }}
            >
                <slot />
            </Host>
        );
    }
}
